import { firstNameOf, renderTemplate, whatsappLink } from '@takumi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Send } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useInvalidateCrm } from '@/lib/queries';
import type { Contact } from '@/lib/types';

interface Template {
  id: string;
  channel: 'email' | 'whatsapp';
  key: string;
  name: string;
  subject: string | null;
  body: string;
  waTemplateName: string | null;
  isActive: boolean;
}

export function useMessagingStatus() {
  return useQuery({
    queryKey: ['messagingStatus'],
    queryFn: () => api.get<{ email: { missing: string[] }; whatsapp: { missing: string[] } }>('/messages/status'),
    staleTime: 5 * 60_000,
  });
}

/**
 * WhatsApp (official Cloud API) or email. When the channel is not connected, the dialog says so ("נדרש חיבור")
 * and offers the manual fallback (wa.me / mailto) — nothing pretends to be sent.
 */
export function SendMessageDialog({
  channel,
  open,
  onOpenChange,
  organizationId,
  organizationName,
  contacts,
  onFallbackLogged,
  quoteId,
  defaultTemplateKey,
}: {
  channel: 'email' | 'whatsapp';
  open: boolean;
  onOpenChange: (o: boolean) => void;
  organizationId: string;
  organizationName: string;
  contacts: Contact[];
  onFallbackLogged?: () => void;
  quoteId?: string | null;
  defaultTemplateKey?: string;
}) {
  const { me } = useAuth();
  const invalidate = useInvalidateCrm();
  const status = useMessagingStatus();
  const templates = useQuery({ queryKey: ['templates', channel], queryFn: () => api.get<Template[]>('/messages/templates', { channel }), enabled: open });
  const eligible = useMemo(() => contacts.filter((c) => (channel === 'whatsapp' ? c.whatsappE164 : c.email)), [contacts, channel]);
  const [contactId, setContactId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [text, setText] = useState('');
  const [subject, setSubject] = useState('');
  const [windowClosed, setWindowClosed] = useState(false);

  const missing = status.data?.[channel].missing ?? [];
  const connected = status.data ? missing.length === 0 : false;
  const contact = eligible.find((c) => c.id === contactId);
  const template = templates.data?.find((t) => t.id === templateId);

  useEffect(() => {
    if (!open) return;
    setContactId((eligible.find((c) => c.isPrimary) ?? eligible[0])?.id ?? '');
    setWindowClosed(false);
    setText('');
    setSubject('');
    setTemplateId('');
  }, [open, eligible]);

  useEffect(() => {
    if (!open || !templates.data) return;
    const t = templates.data.find((x) => x.key === defaultTemplateKey && x.isActive) ?? (channel === 'whatsapp' ? templates.data.find((x) => x.isActive) : undefined);
    if (t) {
      setTemplateId(t.id);
      if (channel === 'email') {
        setSubject(t.subject ?? '');
        setText(t.body);
      }
    }
  }, [open, templates.data, defaultTemplateKey, channel]);

  const vars = { first_name: firstNameOf(contact?.fullName) || '', business_name: organizationName, user_name: me?.user.fullName ?? '', quote_link: quoteId ? '[קישור להצעה]' : '' };
  const preview = renderTemplate(channel === 'whatsapp' ? (template?.body ?? text) : text, vars);

  const send = useMutation({
    mutationFn: () =>
      channel === 'whatsapp'
        ? api.post('/messages/whatsapp', { organizationId, contactId, templateId: templateId || null, text: templateId ? null : text })
        : api.post('/messages/email', { organizationId, contactId, subject, body: text, templateId: templateId || null, quoteId: quoteId ?? null }),
    onSuccess: () => {
      invalidate(organizationId);
      toast.success(channel === 'whatsapp' ? 'הודעת WhatsApp נשלחה' : 'האימייל נשלח');
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.body.code === 'WA_WINDOW_CLOSED') {
        setWindowClosed(true);
        return;
      }
      toast.error(errorMessage(e));
    },
  });

  const fallbackHref =
    channel === 'whatsapp' ? whatsappLink(contact?.whatsappE164, preview) : contact?.email ? `mailto:${contact.email}?subject=${encodeURIComponent(renderTemplate(subject, vars))}&body=${encodeURIComponent(preview)}` : null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!contactId) return toast.error(channel === 'whatsapp' ? 'לאף איש קשר אין מספר WhatsApp' : 'לאף איש קשר אין כתובת אימייל');
    send.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={channel === 'whatsapp' ? 'שליחת WhatsApp' : 'שליחת אימייל'} description={organizationName} wide>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {status.data && !connected && (
            <div className="rounded-card border border-today/30 bg-today-bg p-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-today"><AlertTriangle className="size-4" /> נדרש חיבור</p>
              <p className="mt-1 text-ink-soft">
                {channel === 'whatsapp' ? 'WhatsApp Business Cloud API' : 'שרת האימייל (SMTP)'} עדיין לא מחובר. חסר: <span className="ltr">{missing.join(', ')}</span>.
                אפשר לפתוח את ההודעה ב{channel === 'whatsapp' ? '-WhatsApp' : 'תוכנת הדואר'} ולרשום אותה ידנית.
              </p>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="נמען">
              {(id) => (
                <Select id={id} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                  {!eligible.length && <option value="">{channel === 'whatsapp' ? 'אין איש קשר עם WhatsApp' : 'אין איש קשר עם אימייל'}</option>}
                  {eligible.map((c) => <option key={c.id} value={c.id}>{c.fullName} · {channel === 'whatsapp' ? c.whatsappE164 : c.email}</option>)}
                </Select>
              )}
            </Field>
            <Field label="תבנית" hint={channel === 'whatsapp' ? 'מחוץ לחלון 24 השעות — רק תבניות מאושרות' : undefined}>
              {(id) => (
                <Select
                  id={id}
                  value={templateId}
                  onChange={(e) => {
                    setTemplateId(e.target.value);
                    const t = templates.data?.find((x) => x.id === e.target.value);
                    if (t && channel === 'email') {
                      setSubject(t.subject ?? '');
                      setText(t.body);
                    }
                  }}
                >
                  <option value="">{channel === 'whatsapp' ? 'הודעה חופשית (בתוך 24 שעות)' : 'ללא תבנית'}</option>
                  {templates.data?.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              )}
            </Field>
          </div>
          {channel === 'email' && <Field label="נושא" required>{(id) => <Input id={id} value={subject} onChange={(e) => setSubject(e.target.value)} />}</Field>}
          {(channel === 'email' || !templateId) && (
            <Field label="הודעה" required>{(id) => <Textarea id={id} rows={channel === 'email' ? 8 : 4} value={text} onChange={(e) => setText(e.target.value)} />}</Field>
          )}
          <div>
            <div className="mb-1 text-xs font-bold text-muted">תצוגה מקדימה</div>
            <div className="rounded-card border border-line bg-sunken/60 p-3 text-sm whitespace-pre-wrap">{preview || '—'}</div>
          </div>
          {windowClosed && (
            <p className="rounded-control bg-overdue-bg px-3 py-2 text-sm text-overdue">
              עברו יותר מ-24 שעות מההודעה האחרונה של הלקוח. לפי כללי WhatsApp יש לבחור תבנית מאושרת.
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
            {fallbackHref && (
              <Button variant="secondary" asChild>
                <a href={fallbackHref} target="_blank" rel="noopener noreferrer" onClick={() => { onOpenChange(false); onFallbackLogged?.(); }}>
                  <ExternalLink /> {channel === 'whatsapp' ? 'פתיחה ב-WhatsApp ורישום ידני' : 'פתיחה בתוכנת הדואר ורישום ידני'}
                </a>
              </Button>
            )}
            {connected && (
              <Button type="submit" loading={send.isPending} disabled={!contactId}>
                <Send /> שליחה
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
